import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  PORTAL_PASSWORD_MISMATCH_MESSAGE,
  PORTAL_PASSWORD_RULE_MESSAGE,
  portalPasswordValid,
} from '@brimo/shared';
import { Banner, Button, Dialog, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { FieldError, PasswordInput, StrengthBar } from '../activation/PasswordScreen';
import { changePortalPassword, portalKeys } from '../api';
import phone from '../frame/Phone.module.css';
import styles from './Profile.module.css';

/** „Passwort ändern" (Portal 20): current, new with the strength bar, repeat → `POST /api/portal/password`. */
export function PasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function close() {
    setCurrent('');
    setNext('');
    setRepeat('');
    setErrors({});
    setBanner(null);
    onClose();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (!current) found['current'] = 'Bitte geben Sie Ihr bisheriges Passwort ein.';
    if (!portalPasswordValid(next)) found['next'] = PORTAL_PASSWORD_RULE_MESSAGE;
    else if (next !== repeat) found['repeat'] = PORTAL_PASSWORD_MISMATCH_MESSAGE;
    setErrors(found);
    setBanner(null);
    if (Object.keys(found).length > 0) return;
    setPending(true);
    try {
      await changePortalPassword(current, next);
      await queryClient.invalidateQueries({ queryKey: portalKeys.profile });
      await queryClient.invalidateQueries({ queryKey: portalKeys.me });
      toast({ message: 'Ihr Passwort wurde geändert.' });
      close();
    } catch (cause) {
      if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) setErrors(cause.fields);
      else setBanner(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Passwort ändern"
      width={480}
      overlayTop={0}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Abbrechen
          </Button>
          <Button type="submit" form="portal-password-form" loading={pending}>
            Passwort speichern
          </Button>
        </>
      }
    >
      <form
        id="portal-password-form"
        className={styles.dialogForm}
        onSubmit={(event) => void submit(event)}
        noValidate
      >
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <div>
          <label className={phone.label} htmlFor="portal-password-current">
            Bisheriges Passwort
          </label>
          <PasswordInput
            id="portal-password-current"
            value={current}
            onChange={setCurrent}
            invalid={Boolean(errors['current'])}
            autoComplete="current-password"
            autoFocus
          />
          {errors['current'] ? (
            <FieldError id="portal-password-current-error" text={errors['current']} />
          ) : null}
        </div>
        <div>
          <label className={phone.label} htmlFor="portal-password-next">
            Neues Passwort
          </label>
          <PasswordInput
            id="portal-password-next"
            value={next}
            onChange={setNext}
            invalid={Boolean(errors['next'])}
            autoComplete="new-password"
            describedBy="portal-password-next-hint"
          />
          <StrengthBar password={next} />
          {errors['next'] ? (
            <FieldError id="portal-password-next-hint" text={errors['next']} />
          ) : (
            <div id="portal-password-next-hint" className={phone.hint}>
              {PORTAL_PASSWORD_RULE_MESSAGE}
              {portalPasswordValid(next) ? ' Erfüllt.' : ''}
            </div>
          )}
        </div>
        <div>
          <label className={phone.label} htmlFor="portal-password-again">
            Passwort wiederholen
          </label>
          <PasswordInput
            id="portal-password-again"
            value={repeat}
            onChange={setRepeat}
            invalid={Boolean(errors['repeat'])}
            autoComplete="new-password"
          />
          {errors['repeat'] ? <FieldError id="portal-password-again-error" text={errors['repeat']} /> : null}
        </div>
      </form>
    </Dialog>
  );
}
